// Questions and tips, from the everyday kit: the question card and This or
// that? (question posts), the one good tip, the three-step guide and the
// appointment checklist (tip posts).
import { flow, textBlock, brand, footer, rule, panel, display, label, body, strong } from '../../kit'
import { dryPainter } from '../../painter'
import { base, ground, serif, heavy, detailLine, polyline, lines } from './common'

export default {
  // One question, big, and the short answer
  card(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w: w - 240 }), { gap: 60, slot: 'eyebrow' }),
      {
        gap: 12,
        draw: (pp, y, s) => {
          const r = Math.round(100 * s)
          const cx = fr.right - r
          pp.circle(cx, y + r, r, k.accent)
          const size = Math.round(r * 1.45)
          pp.text('?', { ...serif(env, k.on, size), minSize: size, lh: 1, x: cx - r, y: y + r - Math.round(size * 0.47), w: r * 2, align: 'center', maxLines: 1 })
          return y + r * 2 - Math.round(20 * s)
        },
      },
      textBlock(c.headline, s => ({ ...display(env, ink, 116 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 0, flex: 1, slot: 'headline' }),
      rule(fr, ink, { gap: 48 }),
      textBlock('The short answer', () => ({ ...label(env, ink), x: fr.left, w }), { gap: 48 }),
      textBlock(c.body, s => ({ ...body(env, ink, Math.round(52 * s)), lh: 1.2, x: fr.left, w, maxLines: 5 }), { gap: 30, flex: 1, slot: 'body' }),
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },

  // What people think, then what you'd tell them
  myth(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const pad = 30
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 110 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 28, slot: 'headline' }),
      c.thought ? {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.pop, pad,
          content: (q, top) => {
            const l = q.text('The thought', { ...label(env, ink), x: fr.left + pad, y: top, w: w - pad * 2 })
            return q.text(c.thought, { ...serif(env, ink, Math.round(48 * s)), x: fr.left + pad, y: l.bottom + 18, w: w - pad * 2, maxLines: 3, slot: 'thought' }).bottom + 6
          },
        }),
      } : null,
      {
        gap: c.thought ? 24 : 64,
        flex: c.thought ? 0 : 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.accent, pad,
          content: (q, top) => {
            const l = q.text('The conversation', { ...label(env, k.on), x: fr.left + pad, y: top, w: w - pad * 2 })
            return q.text(c.body || '', { ...body(env, k.on, Math.round(46 * s)), lh: 1.22, x: fr.left + pad, y: l.bottom + 18, w: w - pad * 2, maxLines: 5, slot: 'body' }).bottom + 10
          },
        }),
      },
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },

  // One tip, big and bold, with its number
  oneTip(p, env) {
    const { fr, c, w } = base(env)
    const on = ground(p, env, true)
    const head = [String(c.label || '').trim(), String(c.number || '').trim()].filter(Boolean).join(' / ')
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(head, () => ({ ...label(env, on), x: fr.left, w }), { gap: 60, slot: 'label' }),
      textBlock(c.number, s => ({ ...heavy(on, Math.round(200 * s)), x: fr.left, w: w - 60, align: 'right', maxLines: 1 }), { gap: 30, slot: 'number' }),
      textBlock(c.headline, s => ({ ...display(env, on, 128 * s), x: fr.left - 5, w: w + 5, maxLines: 3 }), { gap: 40, flex: 1, slot: 'headline' }),
      rule(fr, on, { gap: 48 }),
      textBlock(c.body, s => ({ ...body(env, on, Math.round(48 * s)), lh: 1.2, x: fr.left, w, maxLines: 5 }), { gap: 44, slot: 'body' }),
      detailLine(env, fr, c.detail, on),
      footer(env, fr, on),
    ])
  },

  // Three short steps on bars of colour
  steps(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const list = lines(c.steps)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.label, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'label' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 116 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 28, slot: 'headline' }),
      list.length ? {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => {
          let yy = y
          list.forEach((t, i) => {
            const bg = i % 2 ? k.pop : k.accent
            const fg = i % 2 ? k.ink : k.on
            const padY = Math.round(34 * s)
            const size = Math.round(48 * s)
            const inside = (q, top) => q.text(t, { ...strong(env, fg, size), x: fr.left + 150, y: top, w: w - 180, maxLines: 2, slot: 'steps' })
            const tt = inside(pp.isDry ? pp : dryPainter(pp), yy + padY)
            const h = Math.max(Math.round(148 * s), tt.bottom - yy + padY)
            pp.rect(fr.left, yy, w, h, bg)
            pp.text(`0${i + 1}`, { ...strong(env, fg, Math.round(56 * s)), x: fr.left + 26, y: yy + padY - 4, w: 110, maxLines: 1 })
            inside(pp, yy + padY)
            yy += h + (i < list.length - 1 ? Math.round(22 * s) : 0)
          })
          return yy
        },
      } : null,
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },

  // Three things to do before they come in, each ticked off
  checklist(p, env) {
    const { fr, c, w } = base(env)
    const ink = ground(p, env, false)
    const list = lines(c.steps)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.label, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'label' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 112 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 28, slot: 'headline' }),
      rule(fr, ink, { gap: 52 }),
      list.length ? {
        gap: 56,
        flex: 1,
        draw: (pp, y, s) => {
          let yy = y
          const box = Math.round(58 * s)
          const tx = fr.left + box + 44
          list.forEach((t, i) => {
            pp.stroke(fr.left, yy, box, box, ink, 3)
            polyline(pp, [[fr.left + box * 0.22, yy + box * 0.52], [fr.left + box * 0.42, yy + box * 0.74], [fr.left + box * 0.8, yy + box * 0.26]], ink, Math.max(4, Math.round(6 * s)))
            const tt = pp.text(t, { ...body(env, ink, Math.round(46 * s)), x: tx, y: yy + Math.round(box / 2) - Math.round(30 * s), w: fr.right - tx, maxLines: 2, slot: 'steps' })
            const end = Math.max(tt.bottom, yy + box) + Math.round(34 * s)
            pp.line(tx, end, fr.right, end, ink, 2)
            yy = end + Math.round(i < list.length - 1 ? 44 * s : 0)
          })
          return yy
        },
      } : null,
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },
}
